<template>
  <div ref="activitySectionRef" class="activity-section">
    <!-- 활동 카드 목록 -->
    <div class="activity-cards">
      <!-- 활동 카드 -->
      <ActivityCard
        v-for="activity in activities"
        :key="`${activity.planId ?? ''}-${activity.id}`"
        :activity-no="activity.id"
        :plan-id="activity.planId"
        :status-nm="activity.statusNm"
        :evaluation="activity.evaluation"
        :current-date="activity.currentDate"
        :activityDt="activity.activityDt"
        :created-date="activity.createdDate"
        :content="activity.content"
        :feedback-list="activity.feedbackList"
        :create-user-id="activity.createUserId"
        :create-user-nm="activity.createUserNm"
        :create-user-dept="activity.createUserDept"
        :read-user-str="activity.readUserStr"
        :read-user-cnt="activity.readUserCnt"
        :activity-unread-cnt="activity.activityUnreadCnt"
        :status-cd="activity.statusCd"
        :statusColor="activity.statusColor || activity.color"
        :result-status-cd="activity.resultStatusCd"
        :result-status-nm="activity.resultStatusNm || activity.statusNm"
        :reply-cnt="activity.replyCnt"
        :atch-file-id="activity.atchFileId"h
        :atch-file-cnt="activity.atchFileCnt"
        :img-paths="activity.imgPaths"
        :thumb-paths="activity.thumbPaths"
        :ibms-activity-id="activity.ibmsActivityId"
        :activity-read-only="props.activityReadOnly"
        :feedback-read-only="props.feedbackReadOnly"
        :show-activity-edit-delete="props.showActivityEditDelete"
        :show-read-status="props.showReadStatus"
        :editor-resizable="props.editorResizable"
        @edit="handleEdit"
        @delete="handleDelete"
        @reply="handleReply"
        @activity-updated="handleActivityUpdated"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { useCollaborationStore } from '~/composables/useCollaborationStore';
import ActivityCard from './ActivityCard.vue';

const { focusActivity, focusActivityScrollOnly } = useCollaborationStore();

const activitySectionRef = ref(null);

// props 정의
const props = defineProps({
  activities: {
    type: Array,
    default: () => [],
  },
  activityReadOnly: {
    type: Boolean,
    default: false,
  },
  feedbackReadOnly: {
    type: Boolean,
    default: false,
  },
  /** activityReadOnly일 때 수정/삭제 버튼 표시 (기본 false = 숨김) */
  showActivityEditDelete: {
    type: Boolean,
    default: false,
  },
  showReadStatus: {
    type: Boolean,
    default: true,
  },
  /** 활동/피드백 에디터를 드래그 리사이즈 모드로 표시 (업무/협업 화면 전용, 기본값 false) */
  editorResizable: {
    type: Boolean,
    default: false,
  },
});

// emit 정의
const emit = defineEmits(['activity-updated']);

// 활동 업데이트 핸들러
const handleActivityUpdated = activityItems => {
  // props는 읽기 전용이므로 직접 수정하지 않고 이벤트만 전달
  emit('activity-updated', activityItems);
};

// focusActivity 변경 시 모달 내 해당 활동/피드백으로 스크롤 (focusActivityScrollOnly면 피드백 작성 버튼 클릭 생략)
const escapeAttrValue = value => {
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return CSS.escape(String(value));
  }
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

/** 활동 본문은 카드 컨테이너, 피드백/대댓글은 comment-section 기준으로 정확히 매칭 */
const findFocusTarget = (root, activityId) => {
  const id = escapeAttrValue(activityId);
  const isFeedback = String(activityId).includes('_');
  if (isFeedback) {
    return root.querySelector(`[data-activity-id="${id}"]`);
  }
  return (
    root.querySelector(`.activity-card-container[data-activity-id="${id}"]`) ||
    root.querySelector(`[data-activity-id="${id}"]`)
  );
};

/**
 * 실제로 스크롤이 걸린 조상을 찾는다.
 *
 * 예전에는 .ui-popup__body 를 먼저 잡았는데, 매니저 대시보드 업무 상세 패널처럼
 * 팝업 본문이 아니라 탭 콘텐츠(.ui-tabs__content)가 스크롤을 갖는 구조에서는
 * 스크롤되지 않는 요소를 잡아 포커스 이동이 통째로 무시됐다.
 * 스크롤 가능한 조상이 없으면 기존 선택자로 되돌아간다.
 */
const getModalScrollContainer = root => {
  let el = root?.parentElement;
  while (el && el !== document.body) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return root?.closest('.ui-popup__body') || root?.closest('.activity-scroll-container') || null;
};

/** scrollIntoView 대신 모달 body 기준 scrollTop — Tinymce 높이 변동 후에도 위치 보정 */
const scrollTargetInModal = (targetElement, scrollContainer) => {
  if (!scrollContainer) {
    targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
    return;
  }
  const targetRect = targetElement.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const scrollTop =
    targetRect.top -
    containerRect.top +
    scrollContainer.scrollTop -
    scrollContainer.clientHeight / 2 +
    targetRect.height / 2;

  scrollContainer.scrollTo({
    top: Math.max(0, scrollTop),
    behavior: 'auto',
  });
};

let focusScrollToken = 0;

const performFocusScroll = async (activityId, scrollOnly) => {
  const token = ++focusScrollToken;
  const retryDelays = [0, 200, 500, 900, 1400];
  let root = null;
  let found = false;

  for (const delay of retryDelays) {
    if (token !== focusScrollToken) return false;
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    await nextTick();

    root = activitySectionRef.value;
    if (!root) continue;

    const targetElement = findFocusTarget(root, activityId);
    if (!targetElement) continue;

    found = true;
    scrollTargetInModal(targetElement, getModalScrollContainer(root));
  }

  if (!found || !root) return false;

  if (!scrollOnly) {
    root.querySelector(`[data-btn-activity-id="${escapeAttrValue(activityId)}"]`)?.click();
  }
  focusActivityScrollOnly.value = false;
  focusActivity.value = '';
  return true;
};

watch(focusActivity, async newValue => {
  if (!newValue || props.activities.length === 0) return;
  await performFocusScroll(newValue, focusActivityScrollOnly.value);
});

// immediate 가 필요하다. 목록이 비어 있는 동안(로딩 중) 이 컴포넌트는 아예 렌더되지 않아서
// (PlanActivityPanel 의 v-if="activities.length > 0"), 목록이 도착하면 새로 마운트된다.
// 그 시점엔 focusActivity 가 이미 세팅돼 있어 두 watch 모두 발화하지 않는다.
// 마운트 직후 한 번 확인해야 "그 달의 활동으로 열기" 같은 사전 지정 포커스가 동작한다.
watch(
  () => props.activities,
  async () => {
    if (!focusActivity.value || props.activities.length === 0) return;
    await performFocusScroll(focusActivity.value, focusActivityScrollOnly.value);
  },
  { deep: true, immediate: true },
);
</script>

<style lang="scss" scoped>
.activity-section {
  margin-top: 24px;

  .activity-cards {
    padding-top: 6px;
    margin-bottom: 6px;
  }

  .comments-section {
    margin-top: 0px;

    .comments-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #333;
    }

    .comments-list {
      margin-bottom: 20px;
      margin-left: 16px;
    }

    .comment-input-area {
      margin-top: 16px;

      .comment-input {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        resize: vertical;
        font-size: 14px;

        &:focus {
          outline: none;
          border-color: #2196f3;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }
      }

      .comment-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 8px;
      }
    }
  }
}

/* 활동 카드 스타일 */
.activity-card {
  border-radius: 8px;
  border: 1px solid #cdd1d5;
  background: #fff;
  padding: 16px;
  margin-bottom: 0;

  .activity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #b1b8be;

    .activity-info {
      .activity-date {
        font-size: 14px;
        color: #666;
        margin-bottom: 4px;

        .activity-type {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 700;

          &--excellent {
            color: #3b82f6;
            position: relative;
            padding-left: 20px;

            &:before {
              content: '';
              position: absolute;
              left: 5px;
              top: 50%;
              transform: translateY(-50%);
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #3b82f6;
            }
          }

          &--completed {
            color: #10b981;
            position: relative;
            padding-left: 20px;

            &:before {
              content: '';
              position: absolute;
              left: 5px;
              top: 50%;
              transform: translateY(-50%);
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #10b981;
            }
          }

          &--average {
            color: #f59e0b;
            position: relative;
            padding-left: 20px;

            &:before {
              content: '';
              position: absolute;
              left: 5px;
              top: 50%;
              transform: translateY(-50%);
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #f59e0b;
            }
          }

          &--poor {
            color: #ef4444;
            position: relative;
            padding-left: 20px;

            &:before {
              content: '';
              position: absolute;
              left: 5px;
              top: 50%;
              transform: translateY(-50%);
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #ef4444;
            }
          }
        }

        .current-date {
          color: #6d7882;
          text-align: right;
          font-size: 14px;
          font-weight: 400;
          line-height: 150%;
        }
      }

      .activity-user {
        font-size: 14px;
        color: #333;

        .separator {
          margin: 0 6px;
          color: #ccc;
        }

        .team {
          color: #464c53;
          font-size: 14px;
          line-height: 150%;
        }

        .user-name {
          color: #464c53;
          font-size: 14px;
          font-weight: 700;
          line-height: 140%;
        }

        .company {
          color: #464c53;
          font-size: 14px;
        }
      }
    }

    .activity-actions {
      display: flex;
      gap: 8px;

      .created-date {
        color: #6d7882;
        text-align: right;
        font-size: 14px;
        font-weight: 400;
        line-height: 150%;
      }

      .action-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        color: #666;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: #f5f5f5;
          color: #333;
        }

        &.edit-btn:hover {
          color: #2196f3;
        }

        &.delete-btn:hover {
          color: #f44336;
        }

        &.reply-btn:hover {
          color: #4caf50;
        }
      }
    }
  }

  .activity-content {
    .activity-list {
      list-style-type: none;
      padding-left: 0;
      margin: 0;

      li {
        padding: 6px 0;
        font-size: 14px;
        color: #555;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}

/* 댓글 카드 스타일 */
.comment-card {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;

  .comment-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #b1b8be;

    .comment-info {
      .comment-date {
        font-size: 14px;
        color: #666;
        margin-bottom: 4px;
      }

      .comment-user {
        font-size: 14px;
        color: #333;

        .separator {
          margin: 0 6px;
          color: #ccc;
        }

        .team {
          color: #464c53;
          font-size: 14px;
          line-height: 150%;
        }

        .user-name {
          color: #464c53;
          font-size: 14px;
          font-weight: 700;
          line-height: 140%;
        }

        .company {
          color: #464c53;
          font-size: 14px;
        }
      }
    }

    .comment-actions {
      display: flex;
      gap: 8px;

      .action-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        color: #666;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: #f0f0f0;
          color: #333;
        }

        &.edit-btn:hover {
          color: #2196f3;
        }

        &.delete-btn:hover {
          color: #f44336;
        }

        &.reply-btn:hover {
          color: #4caf50;
        }
      }
    }
  }

  .comment-content {
    color: #464c53;
    font-size: 16px;
    line-height: 150%;
    word-break: break-word;
  }
}

/* 대댓글 스타일 */
.replies-list {
  margin-top: 12px;
  margin-left: 24px;

  .reply-card {
    background-color: #ffffff;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 8px;
    border-left: 3px solid #e0e0e0;

    .reply-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e5e5e5;

      .reply-info {
        .reply-date {
          font-size: 12px;
          color: #999;
          margin-bottom: 2px;
        }

        .reply-user {
          font-size: 13px;
          color: #333;

          .separator {
            margin: 0 4px;
            color: #ccc;
          }

          .team {
            color: #464c53;
            font-size: 13px;
          }

          .user-name {
            color: #464c53;
            font-size: 13px;
            font-weight: 600;
          }

          .company {
            color: #464c53;
            font-size: 13px;
          }
        }
      }

      .reply-actions {
        display: flex;
        gap: 6px;

        .action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 3px;
          border-radius: 3px;
          color: #888;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            background-color: #f5f5f5;
            color: #555;
          }

          &.edit-btn:hover {
            color: #2196f3;
          }

          &.delete-btn:hover {
            color: #f44336;
          }
        }
      }
    }

    .reply-content {
      color: #464c53;
      font-size: 14px;
      line-height: 140%;
      word-break: break-word;
    }
  }
}

/* 유틸리티 클래스 */
.flex {
  display: flex;
}

.flex-col {
  flex-direction: column;
}

.gap-5 {
  gap: 5px;
}

.items-center {
  align-items: center;
}

.justify-end {
  justify-content: flex-end;
}
</style>
